import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { DateTime } from 'luxon';
import { useQuery, useInfiniteQuery, QueryClientProvider } from '@tanstack/react-query';
import produce from 'immer';
import { useTranslation } from 'react-i18next';
import icon from '../images/ai-percy.png';
import Container from 'react-bootstrap/Container';

const PERCY_VISIT_KEY = 'percy_visit_count';
const PERCY_AUTO_OPEN_VISITS = 3;
const NUDGE_MESSAGES = [
  "Need help? Ask me 🐾",
  "Need a hand with anything?",
  "Need help with training?",
  "Feeling stuck? I can help",
];

function getVisitCount() {
  return parseInt(localStorage.getItem(PERCY_VISIT_KEY) || '0', 10);
}

function incrementVisitCount() {
  const count = getVisitCount() + 1;
  localStorage.setItem(PERCY_VISIT_KEY, String(count));
  return count;
}

const FIRST_TIME_NUDGE = "Not sure where to start? I can help!";

function getNudge(visitCount) {
  if (visitCount <= PERCY_AUTO_OPEN_VISITS) {
    return FIRST_TIME_NUDGE;
  }
  return NUDGE_MESSAGES[Math.floor(Math.random() * NUDGE_MESSAGES.length)];
}

import FullPageSpinner from '../components/common/FullPageSpinner';
import Button from 'react-bootstrap/Button';
import AIAssistantChat from './AIAssistantChat';
import Cta from '../components/common/Cta';
import { getAILogsv2, streamQuestion } from '../api/ai';
import { ME, FAMILY, DOGS, AI_LOGS_V2 } from '../query/queryKeys';
import sortBy from 'lodash/sortBy';
import queryClient from '../query/queryClient';
import store from '../redux/store';

export default function AIAssistant() {
  return (
    <QueryClientProvider client={queryClient}>
      <AIAssistantInner />
    </QueryClientProvider>
  );
}

function ErrorLoading({ reload }) {
  const { t } = useTranslation();
  return (
    <Container className="h-100 d-flex flex-column justify-content-center align-items-center">
      <div className="alert alert-danger m-3">
        {t('heroes.ai-assistant.chatbot-error')}
      </div>
      <Cta onClick={reload}>
        {t('common.reload')}
      </Cta>
    </Container>
  );
}

function introMessage(name, dog) {
  return `
Hi ${name}! I'm Smart Percy 🐾

I'm here to help you and ${dog} get started with separation anxiety training. I can guide you through your first steps, answer any questions, and support you along the way.

Where would you like to begin?
  `;
}

function greeting(name) {
  return `
Hi ${name}! Nice to see you again 👋

What can I help you with today?
  `;
}

function AIAssistantInner() {

  const { t } = useTranslation();
  const [ visitCount ] = useState(() => incrementVisitCount());
  const isMobile = window.innerWidth < 768;
  const shouldAutoOpen = !isMobile && visitCount <= PERCY_AUTO_OPEN_VISITS;
  const [ show, setShow ] = useState(shouldAutoOpen);
  const [ dismissed, setDismissed ] = useState(() => sessionStorage.getItem('percy_dismissed') === '1');
  const [ animateLastMessage, setAnimateLastMessage ] = useState(false);
  const [ showGreeting, setShowGreeting ] = useState(true);
  const [ nudgeMessage, setNudgeMessage ] = useState(null);
  const [ showNudge, setShowNudge ] = useState(false);
  const [ nudgeHiding, setNudgeHiding ] = useState(false);

  useEffect(() => {
    if (!show) {
      const msg = getNudge(visitCount);
      setNudgeMessage(msg);
      // Show after page settles
      const showTimer = setTimeout(() => setShowNudge(true), 3000);
      // Start shrink animation after 8 seconds visible
      const shrinkTimer = setTimeout(() => setNudgeHiding(true), 11000);
      // Remove from DOM after shrink completes
      const hideTimer = setTimeout(() => setShowNudge(false), 11400);
      return () => { clearTimeout(showTimer); clearTimeout(shrinkTimer); clearTimeout(hideTimer); };
    }
  }, []);
  const meQuery = useQuery({ queryKey: ME, refetchOnWindowFocus: false });
  const familyQuery = useQuery({ queryKey: FAMILY, refetchOnWindowFocus: false });
  const dogQuery = useQuery({ queryKey: DOGS, refetchOnWindowFocus: false });

  const query = useInfiniteQuery({
    queryKey: AI_LOGS_V2,
    queryFn: ({ pageParam }) => getAILogsv2(pageParam),
    getNextPageParam: (lastPage) => lastPage.next_page,
    getPreviousPageParam: (firstPage) => firstPage.prev_page,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: show,
  });

  const loading = meQuery?.isLoading || familyQuery?.isLoading || dogQuery?.isLoading || query?.isLoading;
  const hasError = meQuery?.isError || familyQuery?.isError || dogQuery?.isError || query?.isError;

  const user = meQuery?.data;
  const family = familyQuery?.data ?? [];
  const dogs = dogQuery?.data ?? [];

  async function reload() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: AI_LOGS_V2 }),
      queryClient.invalidateQueries({ queryKey: ME }),
      queryClient.invalidateQueries({ queryKey: FAMILY }),
      queryClient.invalidateQueries({ queryKey: DOGS }),
    ]);
  }

  const [streamingMessage, setStreamingMessage] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);

  // Inactivity rating prompt — shown once per 24 hours after 5 min of silence
  const INACTIVITY_MS = 5 * 60 * 1000;
  const RATING_PROMPT_KEY = 'percy_rating_prompt_shown_at';
  const RATING_PROMPT_TTL_MS = 24 * 60 * 60 * 1000;

  function hasShownRatingPromptRecently() {
    const shownAt = localStorage.getItem(RATING_PROMPT_KEY);
    if (!shownAt) return false;
    return Date.now() - parseInt(shownAt, 10) < RATING_PROMPT_TTL_MS;
  }

  function markRatingPromptShown() {
    localStorage.setItem(RATING_PROMPT_KEY, Date.now().toString());
  }

  const [showRatingPrompt, setShowRatingPrompt] = useState(false);
  const [ratingPromptShown, setRatingPromptShown] = useState(() => hasShownRatingPromptRecently());
  const inactivityTimer = useRef(null);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
      inactivityTimer.current = null;
    }
    // Don't re-arm the timer once the prompt has been shown
    if (ratingPromptShown) return;
    inactivityTimer.current = setTimeout(() => {
      setShowRatingPrompt(true);
      setRatingPromptShown(true);
      markRatingPromptShown();
    }, INACTIVITY_MS);
  }, [ratingPromptShown]);

  // Start the inactivity timer when the chat window opens
  useEffect(() => {
    if (show && !ratingPromptShown) {
      resetInactivityTimer();
    }
    return () => {
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
        inactivityTimer.current = null;
      }
    };
  }, [show]);

  // Auto-minimise Percy when the user navigates to a different page.
  // Percy is in a separate React root from BrowserRouter, so we subscribe
  // to the Redux store directly — it tracks pathname for both Turbo and
  // React Router navigations.
  useEffect(() => {
    let prevPathname = store.getState().pathname;
    const unsubscribe = store.subscribe(() => {
      const nextPathname = store.getState().pathname;
      if (nextPathname !== prevPathname) {
        prevPathname = nextPathname;
        setShow(false);
        setShowGreeting(true);
      }
    });
    return unsubscribe;
  }, []);

  const messages = useMemo(() => {
    if (!query.data || !meQuery.data || !dogQuery.data || !familyQuery.data) return [];

    const result = [];
    const flattened = query.data.pages.flatMap((page) => page.logs);
    const sortedMessages = sortBy(flattened, (message) => {
      return DateTime.fromISO(message.created_at).toMillis();
    });

    for (let i = 0; i < sortedMessages.length; i++) {

      const log = sortedMessages[i];
      const userId = log.user_id ?? user.id;
      const author = userId === user?.id ?
        t('heroes.ai-assistant.me') :
        family.find((member) => member.id === userId)?.first_name ?? t('heroes.ai-assistant.family-member');

      if (log.question) {
        result.push({
          id: `${log.id}-question`,
          isMe: userId === user?.id,
          author,
          content: log.question,
          timestamp: log.created_at,
        });
      }

      if (log.answer) {
        // we only want to animate the last incoming response
        const animateTyping = (i === sortedMessages.length - 1) && animateLastMessage;
        result.push({
          id: `${log.id}-answer`,
          isMe: false,
          author: t('heroes.ai-assistant.ai-assistant'),
          content: log.answer,
          timestamp: log.updated_at,
          animateTyping,
        });
      }

    }

    if (result.length === 0) {
      // add a canned introductary message
      result.push({
        id: 'intro',
        isMe: false,
        author: t('heroes.ai-assistant.ai-assistant'),
        content: introMessage(user.first_name, dogs[0].name),
        timestamp: DateTime.now().toISO(),
        animateTyping: true,
      });
    } else {
      // a canned greeting message
      if (showGreeting) {
        result.push({
          id: 'greeting',
          isMe: false,
          author: t('heroes.ai-assistant.ai-assistant'),
          content: greeting(user.first_name),
          timestamp: DateTime.now().toISO(),
          animateTyping: true,
        });

        // only shows once when opening the chat
        setShowGreeting(false);
      }
    }

    return result;

  }, [ query?.data, meQuery?.data, familyQuery?.data, dogQuery?.data, animateLastMessage ]);

  function handleSend(message) {
    setAnimateLastMessage(false);
    setShowGreeting(false);
    resetInactivityTimer();

    // Add the user's message immediately
    const userMessage = {
      id: 'pending-question',
      question: message,
      created_at: DateTime.now().toISO(),
      user_id: user.id,
    };

    queryClient.setQueryData(AI_LOGS_V2, produce((draft) => {
      draft.pages[0].logs.unshift(userMessage);
    }));

    // Start streaming
    setIsStreaming(true);
    setStreamingMessage('');

    streamQuestion(message, {
      onToken: (text) => {
        setStreamingMessage(prev => prev + text);
      },
      onDone: async ({ id, answer }) => {
        setIsStreaming(false);
        setStreamingMessage(null);
        if (!answer || !answer.trim()) {
          const errorMessage = {
            id: 'error',
            updated_at: DateTime.now().toISO(),
            user_id: user.id,
            answer: t('heroes.ai-assistant.ai-error'),
          };
          queryClient.setQueryData(AI_LOGS_V2, produce((draft) => {
            draft.pages[0].logs.unshift(errorMessage);
          }));
          return;
        }
        await queryClient.invalidateQueries({ queryKey: AI_LOGS_V2 });
        resetInactivityTimer();
      },
      onError: (error) => {
        setIsStreaming(false);
        setStreamingMessage(null);
        const errorMessage = {
          id: 'error',
          updated_at: DateTime.now().toISO(),
          user_id: user.id,
          answer: t('heroes.ai-assistant.ai-error'),
        };
        queryClient.setQueryData(AI_LOGS_V2, produce((draft) => {
          draft.pages[0].logs.unshift(errorMessage);
        }));
      },
    });
  }

  function toggleVisibility() {
    setAnimateLastMessage(false);
    setShowGreeting(true);
    setShow(!show);
  }

  function dismissPercy(e) {
    e.stopPropagation();
    sessionStorage.setItem('percy_dismissed', '1');
    setDismissed(true);
  }

  function restorePercy() {
    sessionStorage.removeItem('percy_dismissed');
    setDismissed(false);
    setShow(true);
  }

  function loadMore() {
    if (query.hasNextPage) {
      query.fetchNextPage();
    }
  }

  if (dismissed) return (
    <button className="percy-restore-btn" onClick={restorePercy} aria-label="Open Percy chat">?</button>
  );

  return (
    <>
      {!show ?
        <div className="percy-fab-wrapper">
          { showNudge && nudgeMessage &&
            <div className={`percy-nudge${nudgeHiding ? ' nudge-hiding' : ''}`} onClick={toggleVisibility}>
              {nudgeMessage}
            </div>
          }
          <Button id="ai-assistant-toggle" onClick={toggleVisibility}>
            <img src={icon} alt={t('heroes.ai-assistant.ai-button')} />
          </Button>
          <button className="percy-dismiss-btn" onClick={dismissPercy} aria-label="Hide Percy">×</button>
        </div>
        :
        <div id="ai-assistant-window" className="d-flex flex-column">
          { loading ? (
            <FullPageSpinner />
          ) : hasError ? (
            <ErrorLoading reload={reload}/>
          ) : (
            <AIAssistantChat
              messages={messages}
              onClose={toggleVisibility}
              onSend={handleSend}
              isTyping={isStreaming}
              streamingText={streamingMessage}
              loadMore={loadMore}
              isLoadingMore={query.isFetchingNextPage}
              showRatingPrompt={showRatingPrompt}
              userEmail={user?.email ?? ''}
            />
          )}
        </div>
      }
    </>
  );
}
